<template>
  <fieldset :disabled="hasGovNotifyInstalledApp() && !hasMultipleGovNotifyServicesEnabled">
    <div v-if="hasMultipleGovNotifyServicesEnabled">
      <GovNotifyServiceConfiguration :value="services" :loading="loadingServices" @input="handleServiceChange" />
    </div>
    <div v-else>
      <div class="field">
        <label class="label">Service Name</label>
        <div class="control">
          <input
            class="input"
            type="text"
            placeholder="Enter Service name"
            required
            v-model.trim.lazy="name"
            @change="$v.name.$touch()"
            :class="{ 'is-danger': $v.name.$invalid && $v.name.$dirty }"
          />
          <p class="help is-danger" v-if="$v.name.$invalid && $v.name.$dirty">Name is required</p>
        </div>
      </div>

      <div class="field">
        <label class="label">Phone Number</label>
        <div class="control">
          <input
            class="input"
            type="text"
            placeholder="Enter Phone Number"
            required
            v-model.trim.lazy="phoneNumber"
            @change="$v.phoneNumber.$touch()"
            :class="{ 'is-danger': $v.phoneNumber.$invalid && $v.phoneNumber.$dirty }"
          />
          <p class="help is-danger" v-if="$v.phoneNumber.$invalid && $v.phoneNumber.$dirty">
            Phone Number is required and must be numeric
          </p>
        </div>
      </div>

      <div class="field">
        <label class="label">API Key</label>
        <div class="control">
          <input
            class="input"
            type="text"
            placeholder="Enter API key here"
            required
            :disabled="loadingTemplates"
            v-model.trim.lazy="apiKey"
            @change="
              $v.apiKey.$touch();
              loadTemplates();
            "
            :class="{ 'is-danger': $v.apiKey.$invalid && $v.apiKey.$dirty }"
          />
          <p class="help is-danger" v-if="$v.apiKey.$invalid && $v.apiKey.$dirty">The API key is required</p>
        </div>
      </div>

      <div class="field" v-if="!hasGovNotifyInstalledApp()">
        <label class="label">Choose a Template</label>
        <div class="control">
          <div class="field has-addons">
            <div class="control is-expanded">
              <div
                class="select is-fullwidth"
                :class="{
                  'is-loading': loadingTemplates,
                  'is-danger': $v.selectedTemplate.$invalid && $v.selectedTemplate.$dirty,
                }"
              >
                <select v-model="selectedTemplate" :disabled="!canLoadTemplates()">
                  <option v-for="template in templates" :key="template.id" :value="template">
                    {{ template.name }}
                  </option>
                  <option v-if="templates.length === 0" value="null" selected>&lt;No templates configured&gt;</option>
                </select>
              </div>
              <p class="help is-danger" v-if="$v.selectedTemplate.$invalid && $v.selectedTemplate.$dirty">
                The template is required
              </p>
            </div>
            <p class="control">
              <button class="button is-info" :disabled="!canLoadTemplates()" @click="loadTemplates()">
                <span class="icon">
                  <font-awesome-icon icon="sync"></font-awesome-icon>
                </span>
              </button>
            </p>
          </div>
        </div>
      </div>

      <div class="field" v-if="hasGovNotifyInstalledApp()">
        <label class="label">Template</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select>
              <option>{{ govNotifyApp?.templateName }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">Delivery Receipts Callback URL</label>
        <div class="control">
          <div class="field has-addons">
            <div class="control is-expanded">
              <input class="input" type="text" disabled v-model="deliveryCallbackUrl" />
            </div>
            <p class="control">
              <button class="button is-info" @click="copyToClipboard(deliveryCallbackUrl)">
                <span class="icon">
                  <font-awesome-icon icon="copy"></font-awesome-icon>
                </span>
              </button>
            </p>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">Received Messages Callback URL</label>
        <div class="control">
          <div class="field has-addons">
            <div class="control is-expanded">
              <input class="input" type="text" disabled v-model="messageCallbackUrl" />
            </div>
            <p class="control">
              <button class="button is-info" @click="copyToClipboard(messageCallbackUrl)">
                <span class="icon">
                  <font-awesome-icon icon="copy"></font-awesome-icon>
                </span>
              </button>
            </p>
          </div>
        </div>
      </div>

      <div class="field" v-if="!hasGovNotifyInstalledApp()">
        <label class="label">Bearer Token</label>
        <div class="control">
          <div class="field has-addons">
            <div class="control is-expanded">
              <input class="input" type="text" disabled v-model="bearerToken" />
            </div>
            <p class="control">
              <button class="button is-info" @click="copyToClipboard(bearerToken)">
                <span class="icon">
                  <font-awesome-icon icon="copy"></font-awesome-icon>
                </span>
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>

    <n-button-group>
      <n-button variant="primary" @click="save()" :disabled="submitIsDisabled" :loading="actionInProgress">
        Submit
      </n-button>
      <n-button @click="cancel()" variant="primary--outline">Cancel</n-button>
    </n-button-group>

    <AppConfirmModal
      v-if="showConfirmationModal"
      title="Discard Changes"
      @confirm="
        hasConfirmedDiscard = true;
        cancel(true);
      "
      @cancel="showConfirmationModal = false"
    >
      <p>All changes you've made to your GovNotify Services will be discarded.</p>
      <p>Are you sure you want to cancel?</p>
    </AppConfirmModal>
    <AppConfirmModal
      v-if="showDeleteConfirmationModal"
      title="Delete Services"
      @confirm="save(true)"
      @cancel="showDeleteConfirmationModal = false"
    >
      <p>Are you sure you want to delete the following services? The action is irreversible.</p>
      <ul class="deleted-services">
        <li v-for="service in deletedGovNotifyServices" :key="service.id">
          {{ service.name }}
        </li>
      </ul>
    </AppConfirmModal>
  </fieldset>
</template>

<script lang="ts" setup>
import { required, numeric } from '@vuelidate/validators';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCopy, faSync } from '@fortawesome/free-solid-svg-icons';
import Toast from '@/shared/services/toast';
import { govNotifyApi } from './services/govNotifyApi';
import { GovNotifyInstalledApp, GovNotifyTemplate, InstallGovNotifyRequest } from './models';
import GovNotifyServiceConfiguration from './components/GovNotifyServiceConfiguration.vue';
import { GovNotifyServiceViewModel } from './models/govNotifyServiceViewModel';
import AppConfirmModal from '../shared/components/AppConfirmModal.vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { useClipboard } from '@vueuse/core';

library.add(faCopy);
library.add(faSync);

const { copy } = useClipboard();
const router = useRouter();
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const currentApp = computed(() => store.getters.currentApp);

const messageCallbackUrl: string = govNotifyApi.getMessageWebhookUrl();
const deliveryCallbackUrl: string = govNotifyApi.getDeliveryWebhookUrl();

const deletedGovNotifyServices = ref<GovNotifyServiceViewModel[]>([]);
const govNotifyApp = ref<GovNotifyInstalledApp | null>(null);
const bearerToken = ref('');
const isAppInstalled = ref(false);
const apiKey = ref('');
const name = ref('');
const phoneNumber = ref('');
const serviceId = ref('');
const templates = ref<GovNotifyTemplate[]>([]);
const selectedTemplate = ref<GovNotifyTemplate | null>(null);
const services = ref<GovNotifyServiceViewModel[]>([]);
const loadingTemplates = ref(false);
const loadingServices = ref(true);
const actionInProgress = ref(false);
const showConfirmationModal = ref(false);
const showDeleteConfirmationModal = ref(false);
const hasConfirmedDiscard = ref(false);
const appUnsavedState = ref(false);

const hasMultipleGovNotifyServicesEnabled = computed(
  () => !!currentWorkspace.value.features.multipleIdentitiesPerChannelEnabled,
);

const validations = {
  apiKey: { required },
  selectedTemplate: { required },
  name: { required },
  phoneNumber: { required, numeric },
};

const $v = hasMultipleGovNotifyServicesEnabled.value
  ? useVuelidate()
  : useVuelidate(validations, { apiKey, selectedTemplate, name, phoneNumber });

const emit = defineEmits<{
  (e: 'loaded'): void;
  (e: 'configSaved'): void;
}>();

let routerHookFn: any; // A function that will be called to destroy the router beforeLeave hook.

onBeforeMount(async () => bindRouterHook());
onBeforeUnmount(() => {
  if (typeof routerHookFn === 'function') routerHookFn();
});

onMounted(async () => {
  if (currentApp.value.isInstalled) {
    try {
      await loadApp();
      if (hasMultipleGovNotifyServicesEnabled.value) {
        await loadServices();
      } else if (!isAppInstalled.value) {
        const result = await govNotifyApi.getBearerToken(currentWorkspace.value.id);
        serviceId.value = getServiceIdFromBearerToken(result.bearerToken);
        bearerToken.value = result.bearerToken;
      }
    } finally {
      emit('loaded');
    }
  } else {
    emit('loaded');
  }
});

function bindRouterHook() {
  routerHookFn = router.beforeEach((to, from, next) => {
    try {
      if (appUnsavedState.value && !hasConfirmedDiscard.value) {
        showConfirmationModal.value = true;
      } else {
        next();
      }
    } catch {
      console.warn('Ignore unbind route exception');
    }
  });
}

async function loadApp() {
  try {
    govNotifyApp.value = await govNotifyApi.getInstalledApp(currentWorkspace.value.id);
    isAppInstalled.value = true;

    if (govNotifyApp.value) {
      apiKey.value = govNotifyApp.value.notifyApiKey;
      name.value = govNotifyApp.value.name;
      phoneNumber.value = govNotifyApp.value.phoneNumber;
    }
  } catch (err: any) {
    // if it's not found, then we haven't installed the app fully, so let them fill in the form
    if (err.status === 404) {
      isAppInstalled.value = false;
    }
  }
}

async function loadServices() {
  loadingServices.value = true;
  try {
    services.value = await govNotifyApi.getServices(currentWorkspace.value.id);
  } catch (err: any) {
    if (err.status === 404) {
      services.value = [];
    }
    Toast.showError(err.message);
  } finally {
    loadingServices.value = false;
  }
}

function canLoadTemplates() {
  return !loadingTemplates.value && apiKey.value.length > 0;
}

function loadTemplates() {
  copyToClipboard(bearerToken.value);
  if (!canLoadTemplates()) {
    return;
  }

  loadingTemplates.value = true;

  govNotifyApi
    .getTemplates(currentWorkspace.value.id, apiKey.value)
    .then((res) => {
      templates.value = res.templates;
    })
    .catch((err) => Toast.showError(err.message))
    .finally(() => {
      loadingTemplates.value = false;
      if (templates.value.length === 0 && $v.value.selectedTemplate) {
        $v.value.selectedTemplate.$touch();
      }
    });
}

function copyToClipboard(text: string) {
  copy(text);
  Toast.showInfo('Copied to clipboard');
}

function hasGovNotifyInstalledApp() {
  return govNotifyApp.value !== null;
}

function cancel(force?: boolean) {
  if (appUnsavedState.value && !force) {
    showConfirmationModal.value = true;
    return;
  }
  router.push({ name: 'apps' });
}

function handleServiceChange(changedServices: Array<Partial<GovNotifyServiceViewModel>>) {
  // Append any deleted services to the array so the user needs to confirm they want to delete them.
  deletedGovNotifyServices.value.push(...services.value.filter((s) => !s.isDraft && !changedServices.includes(s)));

  // Set the state of the service the unsaved so the user then needs to confirm they want to discard changes.
  appUnsavedState.value = true;
  hasConfirmedDiscard.value = false;

  // Update the services array
  services.value = changedServices.map(
    (s: Partial<GovNotifyServiceViewModel>) => Object.assign({}, s) as GovNotifyServiceViewModel,
  );
}

const submitIsDisabled = computed(() => $v.value.$invalid);

function save(force?: boolean) {
  // If we have non-draft services which have been deleted, show the confirmation modal.
  if (deletedGovNotifyServices.value.length > 0 && !force) {
    showDeleteConfirmationModal.value = true;
    return;
  }
  if (actionInProgress.value) {
    return;
  }
  actionInProgress.value = true;
  if (hasMultipleGovNotifyServicesEnabled.value) {
    saveMultiService();
  } else {
    saveSingleService();
  }
}

async function saveMultiService() {
  try {
    if (isAppInstalled.value === false) {
      const installRequest = {
        installId: currentApp.value.installedAppId,
        workspaceId: currentWorkspace.value.id,
      } as InstallGovNotifyRequest;
      await govNotifyApi.install(installRequest);
    }
    await govNotifyApi.updateServices(currentWorkspace.value.id, saveServiceRequest.value);
    appUnsavedState.value = false;
    emit('configSaved');
  } catch (err: any) {
    Toast.showError(err.message);
  } finally {
    actionInProgress.value = false;
  }
}

function saveSingleService() {
  if (!selectedTemplate.value) {
    return;
  }

  const request = {
    installId: currentApp.value.installedAppId,
    workspaceId: currentWorkspace.value.id,
    notifyApiKey: apiKey.value,
    templateId: selectedTemplate.value.id,
    templateName: selectedTemplate.value.name,
    templateBody: selectedTemplate.value.body,
    serviceId: serviceId.value,
    name: name.value,
    phoneNumber: phoneNumber.value,
  } as InstallGovNotifyRequest;

  govNotifyApi
    .install(request)
    .then(() => {
      appUnsavedState.value = false;
      emit('configSaved');
    })
    .catch((err) => Toast.showError(err.message))
    .finally(() => (actionInProgress.value = false));
}

const saveServiceRequest = computed<GovNotifyServiceViewModel[]>(() => {
  // ():  {
  return services.value.map((service) => {
    if (!service.isDraft) {
      return service;
    }
    return {
      id: service.id,
      name: service.name,
      phoneNumber: service.phoneNumber,
      templateId: service.selectedTemplate!.id,
      templateName: service.selectedTemplate!.name,
      templateBody: service.selectedTemplate!.body,
      notifyApiKey: service.notifyApiKey,
    };
  });
});

function getServiceIdFromBearerToken(bearerToken: string) {
  const claims = JSON.parse(atob(bearerToken.split('.')[1]));
  return claims.serviceId;
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.button-group {
  margin-top: 50px;
  margin-bottom: 20px;
}

.n-readonly-input__input {
  width: 100%;
}

ul.deleted-services {
  list-style: disc;
  padding-left: 1.5rem;
  margin-top: 0.5rem;
}
</style>
